import { commitEncounterInventorySpawn } from '../shared/spawn-browser-bridge';
import { cardEditFetch, cardEditQuery } from '../shared/card-edit-publication';
import { encounterAuthoringActive } from '../shared/encounter-surface';
import { checkInventoryEnchantment, allowInventoryEnchantment } from '../shared/enchantment-validation-client';
import { readInventoryField } from '../janus-engine/compile/inventory-raw-readers';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { withAsyncDeadline } from '../shared/async-deadline';
import { refuseUnported } from '../janus-engine/unported';
import { tryPlaceManualDigsite } from './digsite-placement';
import { clearInventoryEditDebounces, inventoryEditNeedsRubbleConfirmation, inventoryTemplateVitalityMaximum, readInventoryVitalityBaseline,
    restoreInventoryVitalityControls, saveInventoryVitalityBaseline } from '../shared/inventory-vitality-edit';

type InventorySaveState = {
    pending?: { stamp: string; promise: Promise<void> };
    cancelledStamp?: string;
    cancellation: number;
};

// One in-flight save per form; cancel retires queued calls and unchanged debounce replays.
const inventorySaves = new WeakMap<HTMLElement, InventorySaveState>();

function inventoryEditStamp(container: HTMLElement): string {
    return JSON.stringify(Array.from(container.querySelectorAll<InventoryFormControl>('input, select, textarea'), input => [
        input.id, input.value, 'checked' in input ? input.checked : null,
        'selectedOptions' in input ? Array.from(input.selectedOptions, option => option.value) : null,
    ]));
}

// inventory-actions.ts  inventory / charge / map-marker / ability-light MUTATION handlers
// (template.js cleanup roadmap Tier 3 � T3.2, 2026-06-16).
//
// Byte-faithful port of the inventory-action layer carved out of template.js. Encounter-state
// mutating  ported verbatim; explicit boundary params + DOM casts were added
// for strict TS. Event-handler functions preserve their `event` arg exactly.
//
// Public surface (window-attached  PHP onclick across options/tools/inventory/**, options/game/**,
// options/tables/**, + object-card / player-character-sheet / inventory-edit-form render-bundle onclick
// strings, all runtime-invoked):
//   adjustCharge, adjustShamanMoteToggle, changeCharges, changeInventoryCharges, addMapMarker,
//   addInventoryMarkerItem, addEncounterInventory, modifyInventory, initInventoryModifyFormEnchantmentValidation,
//   toggleInventoryAbilityLight, openEditInventoryForm, openInventoryStatusEffects, update_inventory_pic_id.
//
// initInventoryModifyFormEnchantmentValidation MUST stay window-attached: template.js loadPostFunctions()
// (line ~3793, OUTSIDE the carved range) calls it after every AJAX render (the range-grep gotcha).
//
// Module-local (grep-proven zero refs outside the carved range): setInventoryEnchantmentValidState,
//   setInventoryEnchantmentMeta, validateInventoryEnchantmentFromForm, scheduleInventoryEnchantmentValidation,
//   resolveInventoryEditFormContainer, resetAddEncounterInventoryFormFields, getInputValue, validateNumber.
//
// Loaded AFTER template.js; every dep resolves at call time (onclick / form-button change / AJAX):
// BASE_URL / getCheckboxValue / checkIfValidUUID / escapeHtml / escapeHtmlInventoryEnchantment / setCookie
// (core-helpers); queryData / getSidelogContext / validateContext / loadPostFunctions / ftDebugLog /
// showErrorNotification / displayWarning (template.js); openItemInfo / openSideLog / forceOpenSideLog /
// openSideForm / forceOpenSideInfoPanel / isSideLogFormPaneActive (side-panels-init.js); setTomSelectValue
// (tom-select-init.js); renderEditInventoryForm (inventory-edit-form bundle); syncEncounterLightSourceAbilityButtons /
// ftRefreshEncounterDataAfterGmLightToggle / ensureEncounterInventoryAbilityLightOn (encounter-overlays bundle);
// encounter projection adoption (websocket bundle, guarded).

type InventoryTomSelect = {
	getValue(): unknown;
	setValue?(value: unknown, silent?: boolean): void;
};

type InventoryFormControl = (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) & {
	tomselect?: InventoryTomSelect;
};

function recordOf(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === 'object'
		? value as Record<string, unknown>
		: null;
}

async function planAddedInventory(payload: Record<string, unknown>): Promise<void> {
    const plan = (window as Window & { ftPlanNewInventoryPlacement?: (p: Record<string, unknown>) => Promise<Record<string, unknown>> }).ftPlanNewInventoryPlacement;
    if (!plan) throw new Error('placement:browser-planner-unavailable');
    Object.assign(payload, await plan(payload));
}

function errorMessage(error: unknown): string {
	return error instanceof Error ? error.message : String(error);
}

function eventTargetElement(event: Event | null | undefined): Element | null {
	return event?.target instanceof Element ? event.target : null;
}

function formContainerFromEvent(event: Event): HTMLElement | null {
	return eventTargetElement(event)?.closest<HTMLElement>('.inventory-container-form') ?? null;
}


// ===========================================================================================
// Charges + shaman mote  adjustCharge / adjustShamanMoteToggle / changeCharges / changeInventoryCharges
// (carved from template.js 5679-5846)
// ===========================================================================================

/** The client-truth charge commit (websocket bundle, charge-truth.ts ftCommitCharge): the shared actor-row loader gets the caster's row,
 * the browser decides, charge_truth_commit_inc.php lazily saves; a refusal is shown in words. The legacy adjust_charge_inc.php /
 * change_charges_inc.php / change_inventory_item_charges_inc.php are gone (N6 2026-10-01, R-071…R-073). */
async function commitChargeTruth(input: { kind: 'adjust' | 'set' | 'inventory'; casterId: unknown; encounterId: unknown; itemId: unknown; delta?: unknown; value?: unknown; log?: boolean }): Promise<void> {
	const fn = (window as unknown as { ftCommitCharge?: (i: Record<string, unknown>) => Promise<unknown> }).ftCommitCharge;
	if (typeof fn !== 'function') { displayWarning('Charges cannot be changed on this page. Reload and try again.'); return; }
	await fn({ ...input, casterId: String(input.casterId ?? ''), encounterId: String(input.encounterId ?? ''), itemId: String(input.itemId ?? '') });
}
async function adjustCharge(
    item_id: string,
    adjust_charge_qty: string | number,
    log_adjust_charge = false,
){
	
	const sl_context = getSidelogContext();
	if (!sl_context) return;

	const {
		roll_private,
		campaign_id,
		encounter_id,
		caster_id,
	} = sl_context;


	// Validate required fields
	const rules = {
		roll_private: { type: 'enum', value: roll_private, allowed: ['0', '1', '2'] },		
		caster_id: { type: 'uuid', value: caster_id },
		encounter_id: { type: 'uuid', value: encounter_id, optional: true }, // the loader reads the caster row's own encounter (N6)
		adjust_charge_qty: { type: 'number', value: adjust_charge_qty },
	};
	const validationResult = validateContext(rules, 'adjustCharge');
	if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }	


	void campaign_id; void roll_private;
	await commitChargeTruth({ kind: 'adjust', casterId: caster_id, encounterId: encounter_id, itemId: item_id, delta: adjust_charge_qty, log: !!log_adjust_charge });
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Shaman mote strip: single click toggles toward empty if charged, else +1 (respects max).
// Optimistically flips the CLICKED tile so the toggle shows immediately: adjustCharge only POSTs
// (touches no DOM), and the strip is either server-rendered (no live repaint at all) or repainted
// async on the caster_update WS tick — so without this the active/empty styling + count wouldn't
// change until a reload. Mirrors the class-mechanic slider's oninput feedback; a later WS repaint
// repaints from the fresh blob and is idempotent with this.
async function adjustShamanMoteToggle(
	abilityId: string,
	currentCharges: string | number,
	maxCharges: string | number,
	el?: Element | null,
) {
	const cur = Number(currentCharges);
	const max = Number(maxCharges);
	if (!Number.isFinite(cur) || !Number.isFinite(max) || max < 1) {
		return;
	}
	const delta = cur > 0 ? -1 : 1;
	const next = cur + delta;
	if (next < 0 || next > max) {
		return;
	}
	const tile = el && el.nodeType === 1 ? (el as HTMLElement) : null;
	if (tile) {
		applyShamanMoteOptimistic(tile, String(abilityId), next, max);
	}
	await adjustCharge(abilityId, String(delta), true);
}

// Repaint one mote tile to `next` charges in place: flip active/empty, update the count text, and
// rewrite the baked-in `current` arg so the NEXT click on this same tile computes from the new value
// (needed in the server-rendered strip, which never gets a fresh-blob repaint to correct the arg).
function applyShamanMoteOptimistic(tile: HTMLElement, abilityId: string, next: number, max: number) {
	const active = next > 0;
	tile.classList.toggle('ft-shaman-mote--active', active);
	tile.classList.toggle('ft-shaman-mote--empty', !active);
	const count = tile.querySelector('.ft-shaman-mote-count');
	if (count && max > 1) {
		count.textContent = `${next} / ${max}`;
	}
	tile.setAttribute('onclick', `adjustShamanMoteToggle('${abilityId}', ${next}, ${max}, this);`);
	tile.setAttribute('onkeydown', `if(event.key==='Enter'||event.key===' '){event.preventDefault();adjustShamanMoteToggle('${abilityId}', ${next}, ${max}, this);}`);
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////



////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function changeCharges(
    item_id: string,
    charge_ability_input_id: string,
){

	const sl_context = getSidelogContext();
	if (!sl_context) return;

	const {
		roll_private,
		campaign_id,
		encounter_id,
		caster_id,
	} = sl_context;


	// Validate required fields
	const rules = {
		roll_private: { type: 'enum', value: roll_private, allowed: ['0', '1', '2'] },		
		encounter_id: { type: 'uuid', value: encounter_id, optional: true }, // the loader reads the caster row's own encounter (N6)
		caster_id: { type: 'uuid', value: caster_id },		
		item_id: { type: 'uuid', value: item_id },
	};
	const validationResult = validateContext(rules, 'changeCharges');
	if (!validationResult.valid) {
        displayWarning(validationResult.error);
        return;
    }	
	
	const new_charges = (document.getElementById(charge_ability_input_id) as HTMLInputElement).value;	

	void campaign_id; void roll_private;
	await commitChargeTruth({ kind: 'set', casterId: caster_id, encounterId: encounter_id, itemId: item_id, value: new_charges });
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////



////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function changeInventoryCharges(
    inventory_id: string,
    charge_ability_input_id: string,
){

	const sl_context = getSidelogContext();
	if (!sl_context) return;

	const {
		roll_private,
		campaign_id,
		encounter_id,
		caster_id,
	} = sl_context;

	// Validate required fields
	const rules = {
		roll_private: { type: 'enum', value: roll_private, allowed: ['0', '1', '2'] },		
		encounter_id: { type: 'uuid', value: encounter_id, optional: true }, // the loader reads the caster row's own encounter (N6)
		caster_id: { type: 'uuid', value: caster_id },		
		inventory_id: { type: 'uuid', value: inventory_id },
	};
	const validationResult = validateContext(rules, 'changeInventoryCharges');
	if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }
	
	const new_charges = (document.getElementById(charge_ability_input_id) as HTMLInputElement).value;

	void campaign_id; void roll_private;
	await commitChargeTruth({ kind: 'inventory', casterId: caster_id, encounterId: encounter_id, itemId: inventory_id, value: new_charges });
}


// ===========================================================================================
// Map markers + add/modify inventory + enchantment-validation cluster + form helpers
// (carved from template.js 5972-6900)
// ===========================================================================================
async function addMapMarker(event: Event, mapMarkerInventoryItemId: string) {
    ftDebugLog('addMapMarker', { called: true });

    const container = formContainerFromEvent(event);
    if (!container) {
        console.error('No valid inventory container found.');
        showErrorNotification('No valid inventory container found.');
        return;
    }

    try {
        // Manually sync Quill content
        ['inventory_description', 'inventory_flavor_text', 'inventory_gm_notes'].forEach(id => {
            const editor = container.querySelector<HTMLElement>(`.quill-editor[data-layer-type-name="${id}"]`);
            const textarea = container.querySelector<HTMLTextAreaElement>(`#${id}`);
            if (editor && textarea) {
                const quill = Quill.find(editor);
                if (quill) {
                    textarea.value = quill.root.innerHTML;
                    ftDebugLog('inventoryForm', { syncedQuill: id, valueLen: textarea.value?.length });
                } else {
                    console.error(`No Quill instance found for ${id}`);
                }
            } else {
                console.error(`Editor or textarea missing for ${id}`, { editor, textarea });
            }
        });

        const sl = getSidelogContext();
        let campaign_id = sl?.campaign_id || (document.getElementById('input_campaign_id') as HTMLInputElement)?.value || null;
		let encounter_id = sl?.encounter_id || null;
		let location_id = sl?.location_id || null;
		

		// check if mapMarkerType and is valid
		if(
			!checkIfValidUUID(mapMarkerInventoryItemId)
		){
			displayWarning("mapMarkerInventoryItemId UUID is not valid");
			return; // break out early
		}
		
		
		// map markers array
		const mapMarkersArr = {
			'N': 'F5DDC0AA-581A-8E6F-EFAF-4445D1A3EBC3',
			'NE': 'ACE453E8-5725-AB18-0BCF-589DDA35135D',
			'E': 'C7814403-F84C-F6F9-0794-95F3EC3AE394',
			'SE': 'D93EF021-9229-85C0-46DB-43F0E7E0FE9D',
			'S': '85436C19-426D-491A-E049-42731CF8AB70',
			'SW': '73F0A293-AE4D-5E0C-9A0A-59F46FE20F5D',
			'W': '44B43903-216C-386A-24BE-5DE3BCBB8E0D',
			'NW': 'BF941177-6C2F-4EF6-92C8-BF45E0E1BE52'
		};
		

		// Check if the UUID exists in the mapMarkersArr values
		if (!Object.values(mapMarkersArr).includes(mapMarkerInventoryItemId.toUpperCase())) {
			displayWarning('UUID not found in map markers');
			return false;
		}
		
	
        // Collect form data
        const formData = {
            campaign_id: campaign_id,
            encounter_id: encounter_id, // Can be blank or null
            location_id: location_id, // Can be blank or null
            inventory_name: getInputValue(container, 'inventory_name'), // Can be blank or null
            inventory_item_id: mapMarkerInventoryItemId,
            enchantment_template_id: getInputValue(container, 'enchantment_template_id'),
            item_qty: validateNumber(container, 'item_qty', 'Quantity must be a positive number', 1),
            inventory_cost_gp: validateNumber(container, 'inventory_cost_gp', 'Cost must be a positive number', 0, true), // Can be blank or null
            inventory_locked: getCheckboxValue('inventory_locked'), // Using existing function
            inventory_lock: getInputValue(container, 'inventory_lock'),
            inventory_private: getCheckboxValue('inventory_private'), // Using existing function
            inventory_unidentified: getCheckboxValue('inventory_unidentified'), // Using existing function
            x_pos: validateNumber(container, 'inventory_x_pos', 'X position must be a number', -10000),
            z_pos: validateNumber(container, 'inventory_z_pos', 'Z position must be a number', -10000),
            inventory_description: getInputValue(container, 'inventory_description'),
            inventory_flavor_text: getInputValue(container, 'inventory_flavor_text'),
            inventory_gm_notes: getInputValue(container, 'inventory_gm_notes'),
        };

        const inventoryKeepSettings = getCheckboxValue('inventory_keep_settings');

        ftDebugLog('addMapMarker', { formData, payload: { ...formData } });

        // Send request
        const payload = { ...formData };

        if (!await allowInventoryEnchantment(String(payload.inventory_item_id ?? ''), String(payload.enchantment_template_id ?? ''))) return;
        const result = await commitEncounterInventorySpawn(payload);

        ftDebugLog('addMapMarker', { resultLen: result?.length });

        const sideLog = (document.getElementById('div_sidelog_warning') as HTMLInputElement);
        if (sideLog) {
            sideLog.innerHTML = result;
        } else {
            console.error('div_sidelog_warning not found');
        }

        // Update UI
        loadPostFunctions();

    } catch (error: unknown) {
        console.error('Error in addMapMarker:', error);
        showErrorNotification(`Failed to add mapMarker: ${errorMessage(error)}`);
    }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function addInventoryMarkerItem(
	event: Event,
	inventory_item_id: string,
	customInventoryName: string,
	inventoryAbilityLightId?: string,
) {
    ftDebugLog('addInventoryMarkerItem', { called: true });

    const container = formContainerFromEvent(event);
    if (!container) {
        console.error('No valid inventory container found.');
        showErrorNotification('No valid inventory container found.');
        return;
    }

    try {
        // Manually sync Quill content
        ['inventory_description', 'inventory_flavor_text', 'inventory_gm_notes'].forEach(id => {
            const editor = container.querySelector<HTMLElement>(`.quill-editor[data-layer-type-name="${id}"]`);
            const textarea = container.querySelector<HTMLTextAreaElement>(`#${id}`);
            if (editor && textarea) {
                const quill = Quill.find(editor);
                if (quill) {
                    textarea.value = quill.root.innerHTML;
                    ftDebugLog('inventoryForm', { syncedQuill: id, valueLen: textarea.value?.length });
                } else {
                    console.error(`No Quill instance found for ${id}`);
                }
            } else {
                console.error(`Editor or textarea missing for ${id}`, { editor, textarea });
            }
        });

        const sl = getSidelogContext();
        let campaign_id = sl?.campaign_id || (document.getElementById('input_campaign_id') as HTMLInputElement)?.value || null;
		let encounter_id = sl?.encounter_id || null;
		let location_id = sl?.location_id || null;
		
		// Validate inventory_item_id UUID
		if (!checkIfValidUUID(inventory_item_id)) {
			displayWarning("inventory_item_id UUID is not valid");
			return;
		}
		
		// Validate customInventoryName
		if (!customInventoryName || typeof customInventoryName !== 'string') {
			displayWarning("Custom inventory name is invalid");
			return;
		}

        // Collect form data
        const formData: Record<string, unknown> = {
            campaign_id: campaign_id,
            encounter_id: encounter_id, // Can be blank or null
            location_id: location_id, // Can be blank or null
            inventory_name: customInventoryName, // Use passed custom name
            inventory_item_id: inventory_item_id, // Use passed door UUID
            enchantment_template_id: getInputValue(container, 'enchantment_template_id'),
            item_qty: validateNumber(container, 'item_qty', 'Quantity must be a positive number', 1),
            inventory_cost_gp: validateNumber(container, 'inventory_cost_gp', 'Cost must be a positive number', 0, true), // Can be blank or null
            inventory_locked: getCheckboxValue('inventory_locked'), // Using existing function
            inventory_lock: getInputValue(container, 'inventory_lock'),
            inventory_private: getCheckboxValue('inventory_private'), // Using existing function
            inventory_unidentified: getCheckboxValue('inventory_unidentified'), // Using existing function
            x_pos: validateNumber(container, 'inventory_x_pos', 'X position must be a number', -10000),
            z_pos: validateNumber(container, 'inventory_z_pos', 'Z position must be a number', -10000),
            inventory_description: getInputValue(container, 'inventory_description'),
            inventory_flavor_text: getInputValue(container, 'inventory_flavor_text'),
            inventory_gm_notes: getInputValue(container, 'inventory_gm_notes'),
        };

        if (inventoryAbilityLightId && checkIfValidUUID(inventoryAbilityLightId)) {
            formData.inventory_ability_light_id = inventoryAbilityLightId;
        }

        const inventoryKeepSettings = getCheckboxValue('inventory_keep_settings');

        ftDebugLog('addInventoryMarkerItem', { formData, payload: { ...formData } });

        // Send request
        const payload = { ...formData };

        if (!await allowInventoryEnchantment(String(payload.inventory_item_id ?? ''), String(payload.enchantment_template_id ?? ''))) return;
        const result = await commitEncounterInventorySpawn(payload);

        ftDebugLog('addInventoryMarkerItem', { resultLen: result?.length });

        const sideLog = (document.getElementById('div_sidelog_warning') as HTMLInputElement);
        if (sideLog) {
            sideLog.innerHTML = result;
        } else {
            console.error('div_sidelog_warning not found');
        }

        const encDomIdPlaceLight = encounter_id || '';
        const campaignDomPlaceLight = campaign_id || '';
        let placedInvFromRsp = '';
        let placedAbiFromRsp = '';
        try {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = result.trim();
          const mk = tmpl.content.querySelector('[data-ft-added-encounter-inventory-id]');
          const candInv = mk?.getAttribute('data-ft-added-encounter-inventory-id') ?? '';
          if (mk && candInv !== '' && checkIfValidUUID(candInv)) {
            placedInvFromRsp = candInv;
            placedAbiFromRsp = mk.getAttribute('data-ft-added-ability-light-id') ?? '';
          }
        } catch (_e: unknown) {}

        if (
          inventoryAbilityLightId
          && checkIfValidUUID(inventoryAbilityLightId)
          && encDomIdPlaceLight !== ''
          && checkIfValidUUID(encDomIdPlaceLight)
          && placedInvFromRsp !== ''
          && placedAbiFromRsp !== ''
          && placedAbiFromRsp.toLowerCase() === String(inventoryAbilityLightId).toLowerCase()
        ) {
          await ensureEncounterInventoryAbilityLightOn(
            placedInvFromRsp,
            inventoryAbilityLightId,
            campaignDomPlaceLight !== '' ? campaignDomPlaceLight : undefined,
            encDomIdPlaceLight,
            true,
          );
        } else if (
          inventoryAbilityLightId
          && checkIfValidUUID(inventoryAbilityLightId)
          && encDomIdPlaceLight !== ''
          && checkIfValidUUID(encDomIdPlaceLight)
        ) {
          await ftRefreshEncounterDataAfterGmLightToggle(encDomIdPlaceLight);
          syncEncounterLightSourceAbilityButtons();
        }

        // Update UI
        loadPostFunctions();

    } catch (error: unknown) {
        console.error('Error in addInventoryMarkerItem:', error);
        showErrorNotification(`Failed to add inventory marker item: ${errorMessage(error)}`);
    }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


/** The native object-birth success sentinel, or '' when no row was saved. */
function addedEncounterInventoryIdFromResponse(result: string): string {
    const tmpl = document.createElement('template');
    tmpl.innerHTML = result.trim();
    const id = tmpl.content.querySelector('[data-ft-added-encounter-inventory-id]')
        ?.getAttribute('data-ft-added-encounter-inventory-id') ?? '';
    return checkIfValidUUID(id) ? id : '';
}

function flashEncounterInventoryAdded(event: Event) {
    const button = (event.target as Element | null)?.closest<HTMLButtonElement>('button');
    if (!button || button.dataset.ftAddedFlash === '1') return;
    const original = button.innerHTML;
    button.dataset.ftAddedFlash = '1';
    // The search picker's row "+" is icon-sized; only a labelled button gets the word.
    button.textContent = (button.textContent ?? '').trim().length > 2 ? '✓ Added' : '✓';
    window.setTimeout(() => {
        button.innerHTML = original;
        delete button.dataset.ftAddedFlash;
    }, 1800);
}

/**
 * The outermost large floating panel (Workspace window / Edit Scene tool panel) holding the clicked control.
 * Viewport-filling mounts (the scene's own full-screen shell) are containers, not panels, and are skipped.
 */
function floatingPanelRect(from: Element | null): DOMRect | null {
    let found: DOMRect | null = null;
    for (let node = from instanceof HTMLElement ? from : null; node && node !== document.body; node = node.parentElement) {
        const position = getComputedStyle(node).position;
        if (position !== 'fixed' && position !== 'absolute') continue;
        const rect = node.getBoundingClientRect();
        const fillsViewport = rect.width >= window.innerWidth * 0.9 && rect.height >= window.innerHeight * 0.9;
        if (rect.width > 200 && rect.height > 150 && !fillsViewport) found = rect;
    }
    return found;
}

/**
 * Where in the scene the new object should land (viewport fractions): the centre of the largest strip
 * (left/right/above/below) the panel leaves uncovered, or the scene centre when the panel does not cover it.
 */
function uncoveredSceneFocus(scene: DOMRect, panel: DOMRect | null): { x: number; y: number } {
    const centre = { x: 0.5, y: 0.5 };
    if (!panel || scene.width <= 0 || scene.height <= 0) return centre;
    const cx = scene.left + scene.width / 2;
    const cy = scene.top + scene.height / 2;
    if (cx < panel.left || cx > panel.right || cy < panel.top || cy > panel.bottom) return centre;
    const strips = [
        { area: Math.max(0, panel.left - scene.left) * scene.height, x: (scene.left + Math.max(scene.left, panel.left)) / 2, y: cy },
        { area: Math.max(0, scene.right - panel.right) * scene.height, x: (Math.min(scene.right, panel.right) + scene.right) / 2, y: cy },
        { area: Math.max(0, panel.top - scene.top) * scene.width, x: cx, y: (scene.top + Math.max(scene.top, panel.top)) / 2 },
        { area: Math.max(0, scene.bottom - panel.bottom) * scene.width, x: cx, y: (Math.min(scene.bottom, panel.bottom) + scene.bottom) / 2 },
    ].sort((a, b) => b.area - a.area);
    const best = strips[0];
    // The panel covers the whole scene: no camera move can reveal it; centre as before.
    if (!best || best.area <= 0) return centre;
    return { x: (best.x - scene.left) / scene.width, y: (best.y - scene.top) / scene.height };
}

/**
 * Ask the campaign FT3D scene to frame the new object; the scene retries once the row is drawn. The form
 * lives either on the campaign page (scene in an iframe) or inside the scene document itself (Edit Scene ›
 * More › Gameplay Inventory) — the scene's message listener serves both.
 */
function focusFt3dSceneOnAddedInventory(inventoryId: string, event: Event) {
    const panel = floatingPanelRect(event.target instanceof Element ? event.target : null);
    const message = (screenFocus: { x: number; y: number }) => ({
        type: 'ft3d-campaign-frame-entity',
        payload: { entityId: inventoryId.toUpperCase(), reason: 'inventory-added', screenFocus },
    });
    const frames = document.querySelectorAll<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    frames.forEach(frame => {
        frame.contentWindow?.postMessage(message(uncoveredSceneFocus(frame.getBoundingClientRect(), panel)), window.location.origin);
    });
    if (frames.length === 0 && document.querySelector('[data-ft-zone-preview-canvas]')) {
        window.postMessage(message(uncoveredSceneFocus(new DOMRect(0, 0, window.innerWidth, window.innerHeight), panel)), window.location.origin);
    }
}

async function addEncounterInventory(event: Event, placement?: { x: number; y: number; z: number } | null) {
    ftDebugLog('addEncounterInventory', { called: true });

    const container = formContainerFromEvent(event);
    if (!container) {
        console.error('No valid inventory container found.');
        showErrorNotification('No valid inventory container found.');
        return;
    }

    try {
        // Manually sync Quill content
        ['inventory_description', 'inventory_flavor_text', 'inventory_gm_notes'].forEach(id => {
            const editor = container.querySelector<HTMLElement>(`.quill-editor[data-layer-type-name="${id}"]`);
            const textarea = container.querySelector<HTMLTextAreaElement>(`#${id}`);
            if (editor && textarea) {
                const quill = Quill.find(editor);
                if (quill) {
                    textarea.value = quill.root.innerHTML;
                    ftDebugLog('inventoryForm', { syncedQuill: id, valueLen: textarea.value?.length });
                } else {
                    console.error(`No Quill instance found for ${id}`);
                }
            } else {
                console.error(`Editor or textarea missing for ${id}`, { editor, textarea });
            }
        });

        const sl = getSidelogContext();
        let campaign_id = sl?.campaign_id || (document.getElementById('input_campaign_id') as HTMLInputElement)?.value || null;
		let encounter_id = sl?.encounter_id || null;
		let location_id = sl?.location_id || null;

		if ((!encounter_id || String(encounter_id).trim() === '') && (document.getElementById('hidden_input_modify_encounter_id') as HTMLInputElement)) {
			encounter_id = (document.getElementById('hidden_input_modify_encounter_id') as HTMLInputElement).value || null;
		}
		if ((!location_id || String(location_id).trim() === '') ) {
			const locEl = document.getElementById('encounter_location_id') as InventoryFormControl | null;
			if (locEl) {
				if (locEl.tomselect && typeof locEl.tomselect.getValue === 'function') {
					const lv = locEl.tomselect.getValue();
					const v = Array.isArray(lv) ? (lv.find(Boolean) ?? '') : (lv ?? '');
					location_id = String(v ?? '').trim() || location_id;
				} else if ('value' in locEl) {
					location_id = String(locEl.value || '').trim() || location_id;
				}
			}
		}

        // Collect form data
        const formData = {
            campaign_id: campaign_id,
            encounter_id: encounter_id, // Can be blank or null
            location_id: location_id, // Can be blank or null
            inventory_name: getInputValue(container, 'inventory_name'), // Can be blank or null
            inventory_item_id: getInputValue(container, 'inventory_item_id'),
            enchantment_template_id: getInputValue(container, 'enchantment_template_id'),
            item_qty: validateNumber(container, 'item_qty', 'Quantity must be a positive number', 1),
            inventory_cost_gp: validateNumber(container, 'inventory_cost_gp', 'Cost must be a positive number', 0, true), // Can be blank or null
            inventory_locked: getCheckboxValue('inventory_locked'), // Using existing function
            inventory_lock: getInputValue(container, 'inventory_lock'),
			inventory_hidden: getCheckboxValue('inventory_hidden'), // Using existing function
            inventory_private: getCheckboxValue('inventory_private'), // Using existing function
            inventory_unidentified: getCheckboxValue('inventory_unidentified'), // Using existing function
            x_pos: validateNumber(container, 'inventory_x_pos', 'X position must be a number', -10000),
            z_pos: validateNumber(container, 'inventory_z_pos', 'Z position must be a number', -10000),
            inventory_description: getInputValue(container, 'inventory_description'),
            inventory_flavor_text: getInputValue(container, 'inventory_flavor_text'),
			inventory_gm_notes: getInputValue(container, 'inventory_gm_notes'),
        };

        const inventoryKeepSettings = getCheckboxValue('inventory_keep_settings');

        ftDebugLog('addEncounterInventory', { formData, payload: { ...formData } });

        // A ghost drop supplies the exact floor point. Manual digsites and their buried tree save together.
        const payload: Record<string, unknown> = { ...formData };
        if (placement && ![placement.x, placement.y, placement.z].every(v => Number.isFinite(v))) {
            // An explicit drop that lost its point must not silently land somewhere else.
            showErrorNotification('Could not read the placement point; the item was not added.');
            return;
        }
        if (placement) {
            payload.x_pos = placement.x;
            payload.y_pos = placement.y;
            payload.z_pos = placement.z;
        }

        if (!await allowInventoryEnchantment(String(payload.inventory_item_id ?? ''), String(payload.enchantment_template_id ?? ''))) return;
        let result = await tryPlaceManualDigsite(payload);
        if (result === null) {
            if (placement) await planAddedInventory(payload);
            // The legacy plain-add path treats zero X as unplaced.
            if (placement?.x === 0) payload.x_pos = 0.001;
            result = await commitEncounterInventorySpawn(payload);
        }
        ftDebugLog('addEncounterInventory', { resultLen: result?.length });

        const sideLog = (document.getElementById('div_sidelog_warning') as HTMLInputElement);
        if (sideLog) {
            sideLog.innerHTML = result;
        } else {
            console.error('div_sidelog_warning not found');
        }

        // A plain add spawns near map centre — often under this very Workspace window — so confirm it
        // on the button the GM just pressed and bring the scene camera to the new object.
        const addedInventoryId = addedEncounterInventoryIdFromResponse(result);
        if (addedInventoryId) {
            flashEncounterInventoryAdded(event);
            focusFt3dSceneOnAddedInventory(addedInventoryId, event);
        }

        // Reset form if not keeping settings
        if (!inventoryKeepSettings) {
            resetAddEncounterInventoryFormFields(container);
        }

        // Update UI
        await loadPostFunctions();

        // Adopt the recompiled encounter projection immediately; websocket reconciliation follows.
        const addInvModEncInput = (document.getElementById('hidden_input_modify_encounter_id') as HTMLInputElement);
        if (
            addInvModEncInput
            && checkIfValidUUID(addInvModEncInput.value)
            && typeof getEncounterArr === 'function'
            && typeof handleEncounterUpdate === 'function'
        ) {
            try {
                const fresh = await getEncounterArr(addInvModEncInput.value, true); // bust: adopt the recompiled blob
                if (fresh) {
                    await handleEncounterUpdate({ encounter_arr: fresh });
                }
            } catch (e: unknown) {
                console.error('FT3D encounter update after add encounter inventory:', e);
            }
        }

    } catch (error: unknown) {
        console.error('Error in addEncounterInventory:', error);
        showErrorNotification(`Failed to add inventory: ${errorMessage(error)}`);
    }
}

// escapeHtmlInventoryEnchantment — EXTRACTED to includes/js/core-helpers.js (loaded FIRST in the footer).

/** Same visual style as PHP display_warning() */
function setInventoryEnchantmentValidState(
    container: HTMLElement,
    valid: boolean,
    messageText: string,
    metaUuid = '',
    metaTitle = '',
) {
    const warn = container.querySelector<HTMLElement>('#div_enchantment_template_warning');
    const btn = container.querySelector<HTMLButtonElement>('#btn_modify_inventory_update');
    container.setAttribute('data-inventory-enchantment-valid', valid ? '1' : '0');
    if (warn) {
        if (valid || !messageText) {
            warn.innerHTML = '';
            warn.style.display = 'none';
        } else {
            warn.innerHTML =
                '<h5 class="bg-warning text-dark rounded border border-2 border-danger p-3 my-2">' +
                escapeHtmlInventoryEnchantment(messageText) +
                '</h5>';
            warn.style.display = '';
        }
    }
    if (btn) {
        btn.disabled = !valid;
    }
    setInventoryEnchantmentMeta(container, valid ? metaUuid : '', valid ? metaTitle : '');
}

/** Toggle the resolved template uuid + linked title shown under the enchantment input. */
function setInventoryEnchantmentMeta(container: HTMLElement, uuid: string, displayTitle: string) {
    const meta = container.querySelector<HTMLElement>('[data-ft-inventory-enchant-meta]');
    if (!meta) return;
    const uuidSpan = meta.querySelector<HTMLElement>('[data-ft-inventory-enchant-meta-uuid]');
    const link = meta.querySelector<HTMLAnchorElement>('[data-ft-inventory-enchant-meta-link]');
    const u = typeof uuid === 'string' ? uuid.trim() : '';
    if (!u || !/^[0-9a-fA-F-]{36}$/.test(u)) {
        if (uuidSpan) uuidSpan.textContent = '';
        if (link) {
            link.textContent = '';
            link.href = '#';
        }
        meta.classList.add('d-none');
        return;
    }
    if (uuidSpan) uuidSpan.textContent = u.toUpperCase();
    if (link) {
        const ttl = typeof displayTitle === 'string' ? displayTitle.trim() : '';
        link.textContent = ttl !== '' ? ttl : 'Open enchantment template';
        link.href = `${BASE_URL}item.php?id=${encodeURIComponent(u)}`;
    }
    meta.classList.remove('d-none');
}

let inventoryEnchantmentValidationDebounce: ReturnType<typeof setTimeout> | null = null;

function inventoryFormItemId(container: HTMLElement): string {
    const selected = container.querySelector<InventoryFormControl>('#inventory_item_id')?.value?.trim() || '';
    if (selected) return selected;
    const inventoryId = container.querySelector<InventoryFormControl>('#inventory_id')?.value || '';
    if (inventoryId && typeof window.ftFindFullNestedItem === 'function') {
        try {
            const cached = recordOf(window.ftFindFullNestedItem(inventoryId));
            return String(readInventoryField(cached, 'inventory_item_id') ?? readItemField(cached, 'inventory_item_id') ?? '');
        } catch { /* The caller reports an unavailable item template. */ }
    }
    return '';
}

async function validateInventoryEnchantmentFromForm(container: HTMLElement, pair?: { itemId: string; encId: string }) {
    if (!container) return false;
    const itemId = pair?.itemId ?? inventoryFormItemId(container);
    const encId = pair?.encId ?? container.querySelector<InventoryFormControl>('#enchantment_template_id')?.value?.trim() ?? '';

    if (encId === '') {
        setInventoryEnchantmentValidState(container, true, '');
        return true;
    }
    if (encId.length !== 36) {
        setInventoryEnchantmentValidState(
            container,
            false,
            'Enter a complete enchantment template UUID (36 characters) or clear the field.'
        );
        return false;
    }
    if (!/^[0-9a-fA-F-]{36}$/.test(encId)) {
        setInventoryEnchantmentValidState(container, false, 'Enchantment template ID must be a valid UUID.');
        return false;
    }
    if (!itemId || !/^[0-9a-fA-F-]{36}$/.test(itemId)) {
        setInventoryEnchantmentValidState(container, false, 'Item template ID is missing or invalid.');
        return false;
    }

    try {
        const result = await checkInventoryEnchantment(itemId, encId);
        if (inventoryFormItemId(container) !== itemId
            || container.querySelector<InventoryFormControl>('#enchantment_template_id')?.value?.trim() !== encId) {
            if (pair) window.displayWarning('The item or enchantment changed while validation was loading. Save again to check the current pair.');
            return false;
        }
        if (result.valid) {
            setInventoryEnchantmentValidState(
                container,
                true,
                '',
                encId,
                typeof result.display_title === 'string' ? result.display_title : '',
            );
        } else {
            setInventoryEnchantmentValidState(
                container,
                false,
                typeof result.message === 'string' ? result.message : 'Invalid enchantment template.',
            );
        }
        return result.valid;
    } catch (e: unknown) {
        console.error(e);
        setInventoryEnchantmentValidState(
            container,
            false,
            'Could not validate enchantment template. Check your connection and try again.'
        );
        return false;
    }
}

function scheduleInventoryEnchantmentValidation(event: Event) {
    const container = resolveInventoryEditFormContainer(event);
    if (!container) return;
    if (inventoryEnchantmentValidationDebounce !== null) {
        clearTimeout(inventoryEnchantmentValidationDebounce);
    }
    inventoryEnchantmentValidationDebounce = setTimeout(() => {
        validateInventoryEnchantmentFromForm(container);
    }, 320);
}

function initInventoryModifyFormEnchantmentValidation() {
    const container = document.querySelector('#div_refresh_sidelog_form .inventory-container-form') as HTMLElement | null;
    if (!container || !container.querySelector('#enchantment_template_id')) {
        return;
    }
    if (container.dataset.ftEnchantmentValidationBound === '1') {
        validateInventoryEnchantmentFromForm(container);
        return;
    }
    container.dataset.ftEnchantmentValidationBound = '1';

    const encInput = container.querySelector('#enchantment_template_id');
    const itemSelect = container.querySelector('#inventory_item_id');

    if (encInput) {
        encInput.addEventListener('input', scheduleInventoryEnchantmentValidation);
        encInput.addEventListener('change', (e) => {
            validateInventoryEnchantmentFromForm((e.target as HTMLElement).closest('.inventory-container-form') || container);
        });
    }
    if (itemSelect) {
        itemSelect.addEventListener('change', (e) => {
            validateInventoryEnchantmentFromForm((e.target as HTMLElement).closest('.inventory-container-form') || container);
        });
    }

    validateInventoryEnchantmentFromForm(container);
}

/**
 * Resolve the "Edit inventory" form root. The encounter add-inventory panel also uses
 * .inventory-container-form and duplicates ids (#item_qty, #inventory_name, …).
 * The edit form is the only one that includes #inventory_id.
 */
function resolveInventoryEditFormContainer(event: Event | null | undefined): HTMLElement | null {
    const fromEvent = eventTargetElement(event)?.closest<HTMLElement>('.inventory-container-form') ?? null;
    if (fromEvent?.querySelector?.('#inventory_id')) {
        return fromEvent;
    }
    const sidelog = document.querySelector<HTMLElement>('#div_refresh_sidelog_form .inventory-container-form');
    if (sidelog?.querySelector?.('#inventory_id')) {
        return sidelog;
    }
    const forms = document.querySelectorAll<HTMLElement>('.inventory-container-form');
    for (let i = 0; i < forms.length; i++) {
        const form = forms[i];
        if (form?.querySelector('#inventory_id')) {
            return form;
        }
    }
    return fromEvent || sidelog || document.querySelector<HTMLElement>('.inventory-container-form');
}

async function modifyInventory(event: Event) {
    ftDebugLog('modifyInventory', { called: true });

    const container = resolveInventoryEditFormContainer(event);
    if (!container) {
        console.error('No valid inventory container found.');
        return;
    }
    syncInventoryQuill(container);
    const prepare = (window as Window & {
        ftPrepareInventoryEditValues?: (form: HTMLElement) => import('../inventory-edit-form/index').InventoryEditValues | null;
    }).ftPrepareInventoryEditValues;
    if (!prepare) { displayWarning('inventory-edit:browser-validation-unavailable'); return; }
    const values = prepare(container);
    if (!values) return;
    const stamp = inventoryEditStamp(container);
    let state = inventorySaves.get(container);
    if (!state) {
        state = { cancellation: 0 };
        inventorySaves.set(container, state);
    }
    if (state.cancelledStamp === stamp) return;
    const pending = state.pending;
    if (pending) {
        const cancellation = state.cancellation;
        await pending.promise;
        if (state.cancellation === cancellation && pending.stamp !== stamp) return modifyInventory(event);
        return;
    }
    const save = saveInventoryEdit(container, state, values);
    state.pending = { stamp, promise: save };
    try { await save; } finally { delete state.pending; }
}

function syncInventoryQuill(container: HTMLElement): void {
    // Manually sync Quill content
    ['inventory_description', 'inventory_flavor_text', 'inventory_gm_notes'].forEach(id => {
        const editor = container.querySelector<HTMLElement>(`.quill-editor[data-layer-type-name="${id}"]`);
        const textarea = container.querySelector<HTMLTextAreaElement>(`#${id}`);
        if (editor && textarea) {
            const quill = Quill.find(editor);
            if (quill) {
                textarea.value = quill.root.innerHTML;
                ftDebugLog('inventoryForm', { syncedQuill: id, valueLen: textarea.value?.length });
            } else {
                console.error(`No Quill instance found for ${id}`);
            }
        } else {
            console.error(`Editor or textarea missing for ${id}`, { editor, textarea });
        }
    });
}

async function saveInventoryEdit(container: HTMLElement, state: InventorySaveState,
    values: import('../inventory-edit-form/index').InventoryEditValues): Promise<void> {
    const { v_current, v_max, lockpick_degree_start, lockpick_degree_end } = values;
    const sl = getSidelogContext();
    const roll_private = sl?.roll_private ?? 0;
    let campaign_id = sl?.campaign_id || '';
    let encounter_id = sl?.encounter_id || '';
    const caster_id = window.ftGetActiveCasterId?.() ?? '';

    if (campaign_id === '' && (document.getElementById('input_campaign_id') as HTMLInputElement)) {
        campaign_id = (document.getElementById('input_campaign_id') as HTMLInputElement).value;
    }
    if (encounter_id === '' && (document.getElementById('hidden_input_modify_encounter_id') as HTMLInputElement)) {
        encounter_id = (document.getElementById('hidden_input_modify_encounter_id') as HTMLInputElement).value;
    }

    const inventory_id = container.querySelector<InventoryFormControl>('#inventory_id')?.value || '';
    const inventory_name = container.querySelector<InventoryFormControl>('#inventory_name')?.value || '';
    // Share the lazy-picker cache fallback with validation; both use the exact save pair.
    const inventory_item_id = inventoryFormItemId(container);
	const inventory_cost_gp = container.querySelector<InventoryFormControl>('#inventory_cost_gp')?.value || '';
    const inventory_description = container.querySelector<InventoryFormControl>('#inventory_description')?.value || '';
	const inventory_written_in_language = container.querySelector<InventoryFormControl>('#inventory_written_in_language')?.value || '';
    const inventory_flavor_text = container.querySelector<InventoryFormControl>('#inventory_flavor_text')?.value || '';
    const inventory_gm_notes = container.querySelector<InventoryFormControl>('#inventory_gm_notes')?.value || '';
    const itemQtyRaw = String(container.querySelector<InventoryFormControl>('#item_qty')?.value ?? '').trim();
    const itemQtyParsed = parseInt(itemQtyRaw, 10);
    const item_qty = Number.isFinite(itemQtyParsed) ? itemQtyParsed : 1;
	// Always send a string (never omit key — PHP 8+ fatals on missing index; avoid `||` dropping 0)
	let inventory_inspection_dc_el = container.querySelector<InventoryFormControl>('#inventory_inspection_dc');
	if (!inventory_inspection_dc_el) {
		const sidelogForm = (document.getElementById('div_refresh_sidelog_form') as HTMLInputElement);
		if (sidelogForm) {
			inventory_inspection_dc_el = sidelogForm.querySelector<InventoryFormControl>('#inventory_inspection_dc');
		}
	}
	if (!inventory_inspection_dc_el) {
		inventory_inspection_dc_el = (document.getElementById('inventory_inspection_dc') as HTMLInputElement);
	}
	const inventory_inspection_dc =
		inventory_inspection_dc_el != null ? String(inventory_inspection_dc_el.value ?? '') : '';
    const inventory_unidentified = container.querySelector<HTMLInputElement>('#inventory_unidentified')?.checked ? 1 : 0;
    // Wares checkbox renders only on PC-owned instances; post ONLY when present so an absent
    // field leaves the stored flag alone (the save endpoint mirrors this with array_key_exists —
    // same pattern as inventory_key_link_locks below).
    const waresCheckbox = container.querySelector<HTMLInputElement>('#inventory_wares');
    const inventory_wares = waresCheckbox ? (waresCheckbox.checked ? 1 : 0) : null;
    const enchantment_template_id = container.querySelector<InventoryFormControl>('#enchantment_template_id')?.value || '';
    const v_temp = parseInt(container.querySelector<InventoryFormControl>('#inventory_v_temp')?.value ?? '', 10) || 0;
    const v_max_temp = parseInt(container.querySelector<InventoryFormControl>('#inventory_v_max_temp')?.value ?? '', 10) || 0;
    const vitalityEdit = { v_current, v_max, v_temp, v_max_temp };
    const savedVitality = readInventoryVitalityBaseline(container);
    const templateMax = savedVitality?.templateId === inventory_item_id ? savedVitality.templateMax
        : inventoryTemplateVitalityMaximum(recordOf(window.ftFindFullNestedItem?.(inventory_item_id)));
    const turnToRubble = savedVitality !== null && inventoryEditNeedsRubbleConfirmation(savedVitality, vitalityEdit, templateMax);
    if (turnToRubble) clearInventoryEditDebounces(container);
    if (turnToRubble && !window.confirm('You are about to turn it to rubble.')) {
        restoreInventoryVitalityControls(container, savedVitality);
        window.ftApplyInventoryEditToCache?.(inventory_id, {
            v_current: savedVitality.v_current, v_max: savedVitality.v_max,
            v_temp: savedVitality.v_temp, v_max_temp: savedVitality.v_max_temp,
        });
        state.cancelledStamp = inventoryEditStamp(container);
        state.cancellation++;
        return;
    }
    const x_pos = parseFloat(container.querySelector<InventoryFormControl>('#inventory_x_pos')?.value ?? '') || 0;
    const z_pos = parseFloat(container.querySelector<InventoryFormControl>('#inventory_z_pos')?.value ?? '') || 0;

	const inventory_encounter_links = Array.from(
		container.querySelector<HTMLSelectElement>('#inventory_encounter_links')?.selectedOptions || [],
	).map((option) => option.value);

	// Key ↔ lock links ("Locks this key opens") — only present when the edited row is a key AND
	// the picker has loaded. Post the field ONLY when the select exists: an absent field tells the
	// save endpoint to leave inventory_key_links alone (a bare [] would wipe this key's links).
	const keyLinksSelect = container.querySelector<HTMLSelectElement>('#inventory_key_link_locks');
	const inventory_key_link_locks = keyLinksSelect
		? Array.from(keyLinksSelect.selectedOptions || []).map((option) => option.value)
		: null;

    ftDebugLog('modifyInventory', {
        formValues: {
        roll_private, 
		campaign_id, 
		encounter_id, 
		caster_id, 
		inventory_id,
        inventory_name, 
		inventory_item_id,
		inventory_cost_gp,
		inventory_description, 
		inventory_written_in_language, 
		inventory_flavor_text,
        inventory_gm_notes, 
		item_qty, 
		inventory_inspection_dc,
        inventory_unidentified, 
		enchantment_template_id, 
		v_current, 
		v_max,
        v_temp, 
		v_max_temp, 
		x_pos, 
		z_pos,
		lockpick_degree_start,
		lockpick_degree_end,
		inventory_encounter_links,
        },
    });

    try {
        const payload: Record<string, unknown> = {
			roll_private, 
			campaign_id, 
			encounter_id, 
			caster_id, 
			inventory_id,
			inventory_name, 
			inventory_item_id,
			inventory_cost_gp,
			inventory_description, 
			inventory_written_in_language,
			inventory_flavor_text,
			inventory_gm_notes, 
			item_qty, 
			inventory_inspection_dc,
			inventory_unidentified, 
			enchantment_template_id, 
			v_current, 
			v_max,
			v_temp, 
			v_max_temp, 
			x_pos, 
			z_pos,
			lockpick_degree_start,
			lockpick_degree_end,
			inventory_encounter_links,
        };
        // tools/encounters/ft3d_scene_host.php is the GM editing tool, NOT live play. Editing an
        // encounter's inventory item there (double-click → edit form → auto-save) must not emit an
        // "inventory_updated" side-log roll/log message — that combat-log row only makes sense during
        // a live encounter session. The modify-encounter page is the only host carrying this hidden
        // input (same signal addEncounterInventory / update_inventory_pic_id already key off of), so
        // flag the save and let modify_inventory_inc.php skip insert_log_message() (which is what
        // writes the roll). Live encounter.php has no such input → still logs normally. The delete
        // buttons (object-card / inventory-edit-form bundles) set the same flag on their delete call.
        if (encounterAuthoringActive()) {
            payload.suppress_inventory_log = '1';
        }

        // Key ↔ lock links: only when the picker is on the form (see collection note above).
        if (inventory_key_link_locks !== null) {
            payload.inventory_key_link_locks = inventory_key_link_locks;
        }

        // Wares: only when the checkbox is on the form (see collection note above).
        if (inventory_wares !== null) {
            payload.inventory_wares = inventory_wares;
        }

        // Don't persist BLANK text overrides. inventory_description / inventory_flavor_text /
        // inventory_gm_notes are per-row OVERRIDES; when empty, the item displays the TEMPLATE's
        // items.item_description / items.flavor_text. Writing an empty override (incl. Quill's
        // "<p><br></p>" empty markup) just dirties the row, so omit any that are effectively empty —
        // the save endpoint reads them with `?? null` and the upsert preserves the stored value. A
        // genuinely-set override stays non-empty → still sent (and adding content still saves).
        const __isBlankHtml = (s: unknown) => String(s ?? '')
            .replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').replace(/ /g, '').trim() === '';
        ['inventory_description', 'inventory_flavor_text', 'inventory_gm_notes'].forEach((__k) => {
            if (__isBlankHtml(payload[__k])) {
                delete payload[__k];
            }
        });

        ftDebugLog('modifyInventory', { payload });

        const heldInventory = recordOf(window.ftFindFullNestedItem?.(inventory_id));
        const sameDefinition = heldInventory
            && String(readInventoryField(heldInventory, 'inventory_item_id') ?? '').toUpperCase() === inventory_item_id.toUpperCase()
            && String(readInventoryField(heldInventory, 'enchantment_template_id') ?? '').toUpperCase() === String(enchantment_template_id ?? '').toUpperCase();
        if (!sameDefinition && !await validateInventoryEnchantmentFromForm(container, {
            itemId: inventory_item_id, encId: enchantment_template_id,
        })) return;
        const admission = (window as Window & { ftPlanInventoryEditAdmission?:
            (id: string, encounter: string, desired?: 0 | 1) => Promise<Record<string, unknown>> }).ftPlanInventoryEditAdmission;
        if (!admission) throw new Error('status-admission:browser-plan-required');
        const hidden = container.querySelector<HTMLInputElement>('#inventory_hidden');
        Object.assign(payload, await withAsyncDeadline(() => admission(inventory_id, encounter_id,
            hidden ? (hidden.checked ? 1 : 0) : undefined), 16000)
            .catch(error => { throw new Error(error instanceof Error && error.name === 'TimeoutError'
                ? 'status-admission:plan-timeout' : errorMessage(error)); }));
        const response = await cardEditFetch(`${BASE_URL}options/tools/inventory/modify_inventory_inc.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        ftDebugLog('modifyInventory', { responseStatus: response.status });
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.text();
        ftDebugLog('modifyInventory', { resultLen: result?.length });

		// Success is ONLY a display_success() block (<h5 class="bg-success …">). Anything else is a
		// FAILURE — including raw echoes from the save's guards: valid_uuid() / validate_numeric_data()
		// print plain text then exit(), which is neither bg-success NOR bg-warning. Those used to be
		// swallowed silently here while the optimistic cache patch made the edit LOOK saved (so the DB
		// never updated and a refresh reverted it). Surface any non-success response, and do NOT patch
		// the cache on failure.
		const sidelogWarningEl = (document.getElementById('div_sidelog_warning') as HTMLInputElement);
		const isSuccess = /\bbg-success\b/.test(result);
		if (!isSuccess) {
			if (sidelogWarningEl) {
				const __raw = (result || '').trim();
				sidelogWarningEl.innerHTML = (__raw !== '' && /\bbg-(warning|danger|success)\b/.test(__raw))
					? __raw
					: ('<h5 class="bg-warning text-dark rounded border border-2 border-danger p-3 my-2">'
						+ 'Inventory not saved. '
						+ (typeof escapeHtml === 'function' ? escapeHtml(__raw) : __raw)
						+ '</h5>');
			}
			if (typeof forceOpenSideLog === 'function') {
				forceOpenSideLog();
			} else if (typeof openSideLog === 'function') {
				openSideLog();
			}
			await loadPostFunctions();
			return; // failed save — never patch the cache (would mask the failure + blank fields)
		}

		if (sidelogWarningEl) {
			sidelogWarningEl.innerHTML = result;
		}
        if (savedVitality) saveInventoryVitalityBaseline(container, {
            ...vitalityEdit, templateMax, templateId: inventory_item_id,
        });
        delete state.cancelledStamp;

		// The table's shared installer already adopted/painted the saved row once.
		// Standalone authoring keeps its existing local card update.
		if (response.headers?.get('X-FT-Card-Applied') !== '1' && typeof window.ftApplyInventoryEditToCache === 'function') {
			try {
				window.ftApplyInventoryEditToCache(inventory_id, {
					inventory_name,
					inventory_item_id,
					inventory_cost_gp,
					inventory_description,
					inventory_written_in_language,
					inventory_flavor_text,
					inventory_gm_notes,
					item_qty,
					inventory_inspection_dc,
					inventory_unidentified,
					enchantment_template_id,
					v_current,
					v_max,
					v_temp,
					v_max_temp,
					x_pos,
					z_pos,
					...(lockpick_degree_start !== null && lockpick_degree_end !== null
						? { lockpick_degree_start, lockpick_degree_end } : {}),
					...(inventory_wares !== null ? { inventory_wares } : {}),
				});
			} catch (e: unknown) {
				console.warn('ftApplyInventoryEditToCache failed', e);
			}
		}

        if (turnToRubble) {
            const destroy = (window as unknown as {
                ftTurnObjectToRubble?: (input: { campaignId: string; encounterId: string; inventoryId: string; casterId: string }) => Promise<boolean>;
            }).ftTurnObjectToRubble;
            await destroy?.({ campaignId: campaign_id, encounterId: encounter_id, inventoryId: inventory_id, casterId: caster_id });
        }
        await loadPostFunctions();

    } catch (error: unknown) {
        console.error('Error in modifyInventory:', error);
        if (/^(?:status-admission:|card-edit:)/.test(errorMessage(error))) refuseUnported(errorMessage(error));
        const sidelogWarningEl = (document.getElementById('div_sidelog_warning') as HTMLInputElement);
        if (sidelogWarningEl) {
            sidelogWarningEl.innerHTML =
                '<h5 class="bg-warning text-dark rounded border border-2 border-danger p-3 my-2">' +
                'Failed to update inventory. Please try again.' +
                '</h5>';
        }
        if (typeof forceOpenSideLog === 'function') {
            forceOpenSideLog();
        } else if (typeof openSideLog === 'function') {
            openSideLog();
        }
    }
}

// Helper functions
function getInputValue(container: ParentNode, elementId: string, errorMessage = ''): string | null {
    const element = container.querySelector<InventoryFormControl>(`#${elementId}`);
    if (!element) {
        throw new Error(`Element ${elementId} not found in container`);
    }
    let raw = '';
    if (element.tomselect && typeof element.tomselect.getValue === 'function') {
        const v = element.tomselect.getValue();
        if (Array.isArray(v)) {
            raw = v.filter(Boolean).join(',');
        } else {
            raw = String(v ?? '');
        }
    } else if ('value' in element) {
        raw = String(element.value ?? '');
    }
    const value = raw.trim();
    if (errorMessage && !value) {
        throw new Error(errorMessage);
    }
    return value || null;
}

function validateNumber(
    container: ParentNode,
    elementId: string,
    errorMessage: string,
    minValue = 0,
    allowNull = false,
): number | null {
    const value = getInputValue(container, elementId);
    if (allowNull && (value === null || value === '')) {
        return null;
    }
    const numValue = parseFloat(value ?? '');
    if (isNaN(numValue) || numValue < minValue) {
        throw new Error(errorMessage);
    }
    return numValue;
}

function resetAddEncounterInventoryFormFields(container: ParentNode) {
    const fields: Array<[string, string | boolean, boolean?]> = [
        ['inventory_name', ''],
        ['inv_item_type', ''],
        ['inventory_item_id', ''],
        ['enchantment_template_id', ''],
        ['item_qty', '1'],
        ['inventory_locked', false, true],
        ['inventory_lock', ''],
        ['inventory_private', false, true],
        ['inventory_unidentified', false, true],
        ['random_scroll_spell_level', ''],
        ['random_ammo_homebrew', '1'],
        ['random_ammo_family', 'weighted'],
        ['inventory_description', ''],
        ['inventory_flavor_text', ''],
        ['inventory_gm_notes', ''],
    ];

    fields.forEach(([id, defaultValue, isCheckbox]) => {
        const element = container.querySelector<InventoryFormControl>(`#${id}`);
        if (!element) {
            return;
        }
        if (isCheckbox) {
            if (element instanceof HTMLInputElement) element.checked = Boolean(defaultValue);
            return;
        }
        if (element.tomselect && typeof element.tomselect.setValue === 'function') {
            setTomSelectValue(element, defaultValue, { silent: true });
        } else {
            element.value = String(defaultValue);
        }
    });
}


// ===========================================================================================
// Inventory ability light  toggleInventoryAbilityLight
// (carved from template.js 6949-7025)
// ===========================================================================================
/**
 * GM: toggle one AbilityLightType status on a light_source inventory row (same pathway as add ability in the status form).
 * At most one ability in that catalog may be active; turning one on removes other AbilityLightType rows first.
 */
async function toggleInventoryAbilityLight(
	isOn: boolean,
	statusId: string,
	inventoryId: string,
	abilityId: string,
	otherStatusIds: readonly string[],
	pushCampaignId?: string,
	pushEncounterId?: string,
	skipOpenItemInfo = false,
) {
	const sl = getSidelogContext();
	if (!sl) {
		return;
	}
	const { roll_private, encounter_id } = sl;
	void pushCampaignId;
	const enc = pushEncounterId || encounter_id;

	forceOpenSideLog();
	(document.getElementById('pills_combat_log_tab') as HTMLInputElement)?.click();

	// N6e (2026-10-02, R-065): the status-truth seams (websocket/status-truth.ts — the item's status through the commit's inventory-holder
	// mode); the legacy delete_creature_status_effect_inc.php and add_status_effect.php are both DELETED (A8: the persistent-AoE add is
	// zone-add.ts + the status commit's zone mode).
	const seams = window as unknown as { ftTryCommitStatusDelete?: (i: { statusId: string; scope: 'single' }) => Promise<unknown>;
		ftTryCommitStatusAdd?: (i: { targetId: string; statusItemId: string; casterId: string; durationInt: string; durationType: string; rollPrivate?: number; encounterId?: string }) => Promise<unknown> };
	if (typeof seams.ftTryCommitStatusDelete !== 'function' || typeof seams.ftTryCommitStatusAdd !== 'function') {
		displayWarning('Reload the page to light or snuff this.');
		return;
	}

	if (isOn) {
		await seams.ftTryCommitStatusDelete({ statusId, scope: 'single' });
		if (!skipOpenItemInfo && checkIfValidUUID(inventoryId)) {
			openItemInfo(inventoryId, false);
		}
		return;
	}

	for (const sid of otherStatusIds || []) {
		if (!checkIfValidUUID(sid)) {
			continue;
		}
		await seams.ftTryCommitStatusDelete({ statusId: sid, scope: 'single' });
	}

	const added = await seams.ftTryCommitStatusAdd({ targetId: inventoryId, statusItemId: abilityId, casterId: '', durationInt: '1', durationType: 'permanent',
		rollPrivate: Number(roll_private) || 0, encounterId: enc || undefined });
	if (added === null) displayWarning('That light could not be lit here.');
	if (!skipOpenItemInfo && checkIfValidUUID(inventoryId)) {
		openItemInfo(inventoryId, false);
	}
}


// ===========================================================================================
// Edit-form orchestrator + status-effects opener + inventory pic
// (carved from template.js 8433-8667)
// ===========================================================================================
async function openEditInventoryForm(inventory_id: string) {
    const caster_id = window.ftGetActiveCasterId?.() ?? '';
    const sl = getSidelogContext();
    const location_id = sl?.location_id || '';
    const encounter_id = sl?.encounter_id || '';

	// Same order as openSideForm(): bring the log + Forms tab to the front *before*
	// injecting HTML and running loadPostFunctions(). If queryData runs while the
	// Combat Log pill is still active, isSideLogFormPaneActive() is false — narrow
	// viewport guards won't block, and anything in the post-load hook chain can
	// forceOpenSideInfoPanel() / openItemInfo() and cover the form the user just
	// asked to edit (e.g. edit-inventory from #div_refresh_side_info).
	forceOpenSideLog();
	(document.getElementById('pills_sidelog_form_tab') as HTMLInputElement).click();

    // The PHP form (form_modify_inventory_inc.php) is RETIRED — ALWAYS client-render from the cached
    // CasterCache node (the SAME full-nest node the side-info card renders from, find-cached-item.ts).
    // The two dynamic <select>s are lazy (data-preload-full="false"); encounter-possessed extras (the
    // cross-encounter link picker + map X/Z bounds — the only bits not in the cache) load via ONE JSON
    // data fetch inside the bundle. The save endpoint (modify_inventory_inc.php) re-fetches + re-sanitizes
    // + re-checks permission server-side, so the cache-painted form persists nothing it shouldn't.
    const container = (document.getElementById('div_refresh_sidelog_form') as HTMLInputElement);
    if (!container) {
        return;
    }

    const findCached = () => {
        try {
            // Prefer the SAME resolver the card we opened from uses (ftRenderItemFromCache →
            // resolveCachedItemForRender): findFullNestedItem PLUS thinned-inventory-instance
            // rehydration. A deduped instance — a map marker / door whose shared template was
            // thinned to the root catalog — has no own item_layer_types, so the raw
            // ftFindFullNestedItem (isFull-gated) returns null and the form bails to its
            // placeholder. The resolver merges the catalog template back in so the edit form
            // paints from the same blob the card rendered. Fall back to the raw finder if the
            // resolver isn't on the page yet.
            if (typeof window.ftResolveCachedItemForRender === 'function') {
                const resolved = window.ftResolveCachedItemForRender(inventory_id);
                if (resolved) return resolved;
            }
            return typeof window.ftFindFullNestedItem === 'function'
                ? window.ftFindFullNestedItem(inventory_id)
                : null;
        } catch (e: unknown) {
            return null;
        }
    };

    let cachedInventoryArr = findCached();

    // Cache-miss fallback (rare — the editor opens from an already-rendered card). Refetch the caster blob
    // that carries this row, then retry. There is no PHP form to fall back to anymore.
    if (!cachedInventoryArr && caster_id && typeof window.fetchAndCacheCaster === 'function') {
        try {
            await window.fetchAndCacheCaster(caster_id);
            cachedInventoryArr = findCached();
        } catch (e: unknown) {
            /* non-fatal */
        }
    }

    if (!cachedInventoryArr || typeof window.renderEditInventoryForm !== 'function') {
        const warn = (document.getElementById('div_sidelog_warning') as HTMLInputElement);
        if (warn) {
            warn.innerHTML = '<h5 class="bg-warning text-dark rounded border border-2 border-danger p-3 my-2">'
                + "Couldn't load that item to edit. Try reopening it." + '</h5>';
        }
        if (typeof forceOpenSideLog === 'function') {
            forceOpenSideLog();
        }
        return;
    }

    window.renderEditInventoryForm(cachedInventoryArr, container, {
        caster_id,
        location_id,
        encounter_id,
        campaign_id: sl?.campaign_id || '',
    });
    // Hydrate the freshly-painted form (Tom Select catalog load, Quill editors, enchantment validation,
    // icon / parse-text mounts, drag-drop, selection handlers) — the same tail queryData would have run.
    await loadPostFunctions();
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function openInventoryStatusEffects(inventory_id: string){
	
	// safety
	if(!checkIfValidUUID(inventory_id)) return;
	
    const sl_context = getSidelogContext();
    if (!sl_context) return;

    const {
        encounter_id,
        campaign_id,
    } = sl_context;

	
	// OPEN SIDELOG (force on mobile — explicit user intent)
	forceOpenSideLog();
	
	// CLICK SIDELOG FORMS TAB
	(document.getElementById('pills_sidelog_form_tab') as HTMLInputElement).click();
	
	
	queryData(
		BASE_URL + "options/tools/statuses/forms/form_modify_inventory_status_effects_inc.php", 
		'div_refresh_sidelog_form', 
		{ 
			inventory_id,
			encounter_id,
			campaign_id,			
		}
	);
	
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////



////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function enableDarkMode() {
    // Apply dark theme
    document.documentElement.setAttribute('data-bs-theme', 'dark');

    // Toggle buttons using Bootstrap classes
    const lightBtn = (document.getElementById('btn_enable_light_mode') as HTMLInputElement);
    const darkBtn = (document.getElementById('btn_enable_dark_mode') as HTMLInputElement);

    lightBtn.classList.remove('d-none');
    darkBtn.classList.add('d-none');

    // Store cookie
    setCookie('dark_mode', '1', 9000);
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function enableLightMode() {
    // Apply light theme
    document.documentElement.setAttribute('data-bs-theme', 'light');

    // Toggle buttons
    const lightBtn = (document.getElementById('btn_enable_light_mode') as HTMLInputElement);
    const darkBtn = (document.getElementById('btn_enable_dark_mode') as HTMLInputElement);

    lightBtn.classList.add('d-none');
    darkBtn.classList.remove('d-none');

    // Store cookie
    setCookie('dark_mode', '0', 9000);
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function selectBtnGroupValue(button: HTMLElement, value: string, inputId: string) {
    const buttons = document.querySelectorAll('.btn-group-toggle .btn');
    buttons.forEach(btn => {
        btn.classList.remove('active', 'btn-primary');
    });
    button.classList.add('active', 'btn-primary');
    const input = (document.getElementById(inputId) as HTMLInputElement);
    input.value = value;
    // Manually dispatch change event
    input.dispatchEvent(new Event('change', { bubbles: true }));
    // console.log('Selected:', value); // Optional: for debugging
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// update_encounter_creature_pic_id -- EXTRACTED to the creature-actions bundle (roadmap T3.1).
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function update_inventory_pic_id(
	inventory_id: string,
	pic_type: string,
	pic_id: string,
	button: HTMLElement,
) {
    // Find the closest carousel container
    const container = button.closest('.carousel');

    if (!container) {
        console.error('No valid carousel container found.');
        return;
    }

    // Validate UUID
    if (!checkIfValidUUID(inventory_id)) {
        console.error('Invalid inventory_id.');
        return;
    }
	
	await cardEditQuery(
		BASE_URL + "options/tools/inventory/update_inventory_pic_id.php",
		'div_sidelog_warning', 
		{ 
			inventory_id,
			pic_type,
			pic_id,
		}
	);

	// Update the appropriate hidden input based on pic_type
	const inputSelector = pic_type === 'inventory_presentation_pic_id' ? '.carousel-presentation-pic-id' : '.carousel-thumbnail-pic-id';
	const buttonSelector = pic_type === 'inventory_presentation_pic_id' ? '.set-presentation-pic' : '.set-thumbnail-pic';
	const input = container.querySelector<HTMLInputElement>(inputSelector);
	if (input) {
		input.value = pic_id;
	}

	// Update button visibility for all items
	container.querySelectorAll<HTMLElement>('.carousel-item').forEach((item) => {
		const targetButton = item.querySelector<HTMLElement>(`${buttonSelector}[onclick*="'${pic_id}'"]`);
		if (targetButton) {
			targetButton.style.display = 'none';
		}
		const otherButtons = item.querySelectorAll<HTMLElement>(`${buttonSelector}:not([onclick*="'${pic_id}'"])`);
		otherButtons.forEach((btn) => {
			btn.style.display = '';
		});
	});

}


// ===========================================================================================
// Window attachment  the exact public surface the original top-level functions exposed. Each
// name has at least one caller OUTSIDE the carved range (PHP onclick / cross-bundle onclick /
// loadPostFunctions for initInventoryModifyFormEnchantmentValidation). The 8 enchantment/form
// helpers (setInventoryEnchantment* / validate* / resolve* / reset* / getInputValue / validateNumber)
// stay module-local  grep-proven zero refs outside the carved range.
// ===========================================================================================
// ===========================================================================================
// Door quick-add  size-aware compass placement (encounter inventory widget)
// -------------------------------------------------------------------------------------------
// The door tab-panes (options/tools/inventory/encounter_inventory_inc.php) carry a per-material
// variant map in [data-ft-door-variants] (size -> {id, name}) and a selected size in
// [data-ft-door-size] (default "medium"). The size-toggle buttons call ftSelectDoorSize; the
// compass buttons call ftAddDoorMarker, which resolves the active size and forwards to
// addInventoryMarkerItem with a direction-prefixed name (e.g. "NW Door, Large, Wooden").
// ===========================================================================================
function ftSelectDoorSize(event: Event, sizeKey: string) {
    const btn = eventTargetElement(event)?.closest<HTMLButtonElement>('button');
    const pane = btn?.closest<HTMLElement>('[data-ft-door-pane]');
    if (!pane) {
        console.error('ftSelectDoorSize: no door pane found');
        return;
    }
    pane.setAttribute('data-ft-door-size', sizeKey);
    pane.querySelectorAll<HTMLButtonElement>('[data-ft-door-size-btn]').forEach((b) => {
        const isActive = b.getAttribute('data-ft-door-size-btn') === sizeKey;
        b.classList.toggle('active', isActive);
        b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
}

function ftAddDoorMarker(event: Event) {
    const btn = eventTargetElement(event)?.closest<HTMLButtonElement>('button');
    const pane = btn?.closest<HTMLElement>('[data-ft-door-pane]');
    if (!btn || !pane) {
        console.error('ftAddDoorMarker: no door button/pane found');
        return;
    }

    const dir = btn.getAttribute('data-ft-door-dir') || '';

    let variants: Record<string, { id?: string; name?: string }> = {};
    try {
        variants = JSON.parse(pane.getAttribute('data-ft-door-variants') || '{}');
    } catch (_e: unknown) {
        console.error('ftAddDoorMarker: bad data-ft-door-variants JSON');
    }

    const sizeKey = pane.getAttribute('data-ft-door-size') || 'medium';
    // Fall back to medium, then to whatever size this door type actually has (e.g. Gate is Large-only).
    const variant = variants[sizeKey] || variants['medium'] || Object.values(variants)[0];
    if (!variant || !variant.id) {
        displayWarning('No door is available in the selected size.');
        return;
    }

    const name = dir ? `${dir} ${variant.name}` : (variant.name as string);
    addInventoryMarkerItem(event, variant.id, name, undefined);
}


declare global {
    interface Window {
        ftGetActiveCasterId?: () => string | null;
        ftResolveCachedItemForRender?: (inventoryId: string) => unknown;
        ftSelectDoorSize: typeof ftSelectDoorSize;
        ftAddDoorMarker: typeof ftAddDoorMarker;
        adjustCharge: typeof adjustCharge;
        adjustShamanMoteToggle: typeof adjustShamanMoteToggle;
        changeCharges: typeof changeCharges;
        changeInventoryCharges: typeof changeInventoryCharges;
        addMapMarker: typeof addMapMarker;
        addInventoryMarkerItem: typeof addInventoryMarkerItem;
        addEncounterInventory: typeof addEncounterInventory;
        modifyInventory: typeof modifyInventory;
        initInventoryModifyFormEnchantmentValidation: typeof initInventoryModifyFormEnchantmentValidation;
        toggleInventoryAbilityLight: typeof toggleInventoryAbilityLight;
        openEditInventoryForm: typeof openEditInventoryForm;
        openInventoryStatusEffects: typeof openInventoryStatusEffects;
        update_inventory_pic_id: typeof update_inventory_pic_id;
    }
}

Object.assign(window, {
    ftSelectDoorSize,
    ftAddDoorMarker,
    adjustCharge,
    adjustShamanMoteToggle,
    changeCharges,
    changeInventoryCharges,
    addMapMarker,
    addInventoryMarkerItem,
    addEncounterInventory,
    modifyInventory,
    initInventoryModifyFormEnchantmentValidation,
    toggleInventoryAbilityLight,
    openEditInventoryForm,
    openInventoryStatusEffects,
    update_inventory_pic_id,
});

export {};

window.ftCheckInventoryEnchantment = checkInventoryEnchantment;
window.ftAllowInventoryEnchantment = allowInventoryEnchantment;
